import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import {
  CircularProgress,
  TableContainer,
  TablePagination,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { deleteNotification, getNotification, updateNotification } from ".";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { StyledTableCell, StyledTableRow } from "../../utils/constantData";
import ViewNotfication from "../../components/dialogbox/ViewNotfication";
import { AdminPageLayout, AdminSectionCard, TableRowActions, DeleteConfirmationDialog, AdminAction } from "../../components/admin";
import { ArchiveOutlined, UnarchiveOutlined } from "@mui/icons-material";

export default function NotificationList() {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [loading, setLoading] = React.useState(true);
  const [isViewNotfication, setIsViewNotfication] = React.useState(false);
  const [notficationDetails, setNotificationDetails] = React.useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);
  const [notificationToDelete, setNotificationToDelete] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const [notification, setNotification] = React.useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const { result } = location.state || {};

  const handleDeleteConfirmation = (notification) => {
    setNotificationToDelete(notification);
    setShowDeleteConfirmation(true);
  };

  React.useEffect(() => {
    if (result) {
      toast.success("Notification Sent!", {
        toastId: "success1",
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getNotification();
      const responseData = response.data;
      setLoading(false);
      setNotification(responseData?.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  const navigateToSendNotification = () => {
    navigate(`/notifications/send`);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleDelete = React.useCallback(async () => {
    if (!notificationToDelete) return;
    setDeleting(true);
    try {
      const result = await deleteNotification(notificationToDelete.id);
      if (result) {
        fetchData();
        toast.success("Notification Deleted Successfully!", {
          toastId: "success5",
          closeOnClick: true,
          position: "bottom-right",
          hideProgressBar: false,
          pauseOnHover: true,
          autoClose: 1000,
          theme: "light",
          draggable: true,
          progress: undefined,
        });
        setShowDeleteConfirmation(false);
        setNotificationToDelete(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete notification", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light",
      });
    } finally {
      setDeleting(false);
    }
  }, [notificationToDelete]);

  const handleArchive = React.useCallback(async (id, archive) => {
    try {
      const result = await updateNotification(id, {
        archive: archive === "yes" ? "no" : "yes",
      });
      if (result) {
        fetchData();
        toast.success("Notification Archive", {
          toastId: "success5",
          closeOnClick: true,
          position: "bottom-right",
          hideProgressBar: false,
          pauseOnHover: true,
          autoClose: 1000,
          theme: "light",
          draggable: true,
          progress: undefined,
        });
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  const handleView = (row) => {
    setIsViewNotfication(true);
    setNotificationDetails(row);
  };

  return (
    <AdminPageLayout
      eyebrow="Communication"
      title="Notifications"
      description="Manage broadcast messages, archive old pushes, and keep delivery actions in one premium workspace."
      actions={
        <AdminAction onClick={navigateToSendNotification}>Send Notification</AdminAction>
      }
    >
      <AdminSectionCard
        title="Notification history"
        description="Review sent messages, open the full content, and archive or remove outdated notifications."
        className="admin-table-shell"
      >
        <TableContainer component="div">
          <Table sx={{ minWidth: 700 }} aria-label="notifications table">
            <TableHead>
              <StyledTableCell>Title</StyledTableCell>
              <StyledTableCell>Notification</StyledTableCell>
              <StyledTableCell>Url</StyledTableCell>
              <StyledTableCell align="right">Actions</StyledTableCell>
            </TableHead>
            <TableBody>
              {notification.length !== 0 ? (
                notification
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <StyledTableRow key={row.id}>
                      <StyledTableCell>{row.title}</StyledTableCell>
                      <StyledTableCell>
                        {row.message.length > 70
                          ? `${row.message.substring(0, 70)}...`
                          : row.message}
                      </StyledTableCell>
                      <StyledTableCell>{row.url}</StyledTableCell>
                      <StyledTableCell align="right">
                        <TableRowActions
                          rowId={row.id}
                          onView={() => handleView(row)}
                          onDelete={() => handleDeleteConfirmation(row)}
                          customActions={[
                            {
                              key: "archive",
                              label: row?.archive === "no" ? "Archive" : "Unarchive",
                              icon: row?.archive === "no" ? (
                                <ArchiveOutlined fontSize="small" />
                              ) : (
                                <UnarchiveOutlined fontSize="small" />
                              ),
                              onClick: () => handleArchive(row.id, row?.archive),
                              variant: "secondary",
                            },
                          ]}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
              ) : loading ? (
                <tr>
                  <td colSpan={4} className="text-center py-8">
                    <CircularProgress size={20} className="text-zinc-500" />
                    <span className="ml-2 text-zinc-500">Loading...</span>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-zinc-500">
                    No notifications found.
                  </td>
                </tr>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={notification.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </AdminSectionCard>

      {isViewNotfication && (
        <ViewNotfication
          selectedNotification={notficationDetails}
          open={isViewNotfication}
          setOpen={setIsViewNotfication}
        />
      )}
      <DeleteConfirmationDialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDelete}
        title="Delete Notification"
        itemName={notificationToDelete?.title}
        loading={deleting}
      />
    </AdminPageLayout>
  );
}
